<script lang="ts">
  import { type PlayerInterface, type MediaConfig, PlayerEvent } from '@foundation-player/loader';
  import { createEventDispatcher, getContext } from 'svelte';
  import Play from '../shared/assets/icons/Play.svelte';
  import PlayPause from '../shared/components/PlayPause.svelte';
  import ReadonlyProgress from '../shared/components/ReadonlyProgress.svelte';
  import ReplayButton from '../shared/components/ReplayButton.svelte';
  import ShareButton from '../shared/components/ShareButton.svelte';
  import Volume from '../shared/components/Volume.svelte';
  import type { FocusLayer } from '../shared/focus-manager/focus-types';
  import type { PlayerStateContext } from '../shared/types/player-state';
  import AdIndicator from '../shared/components/AdIndicator.svelte';
  import AdLink from '../shared/components/AdLink.svelte';
  import TextTrackSelector from '../shared/components/TextTrackSelector.svelte';

  export let player: PlayerInterface | undefined = undefined;
  export let currentMediaConfig: MediaConfig | undefined = undefined;
  export let loading = false;
  export let errorOccurred = false;

  const dispatch = createEventDispatcher<{
    load: boolean;
    errorCase: Error;
  }>();

  const {
    activeTextTrack,
    adClip,
    availableTextTracks,
    currentTime,
    durationInSeconds,
    linearAdPlaying,
    muted,
    playing,
    sessionState,
  } = getContext<PlayerStateContext>('playerState');

  /** Whether we're loading the first medium. Needed because for the first
   * medium, we use the muted configuration from the media config, whereas for
   * the following media, we use the state from the player as changed by the
   * user. */
  let firstMedium = true;
  let previousMediaConfig: MediaConfig | undefined = undefined;

  let headlineTimerId: ReturnType<typeof setTimeout>;
  let headlineHidden = false;

  const focusLayer: FocusLayer = 'content';

  let lastSelectedTextTrackLanguage = '';

  $: progressBarLabel = $linearAdPlaying ? 'Zeit in Werbung' : 'Zeit im Video';

  const showHeadlineAndInitializeHiding = (player: PlayerInterface) => {
    clearTimeout(headlineTimerId);
    headlineHidden = false;

    player.listenOnce(PlayerEvent.TimeChanged, () => {
      headlineTimerId = setTimeout(() => {
        headlineHidden = true;
      }, 3000);
    });
  };

  const loadMedium = (player: PlayerInterface, mediaConfig: MediaConfig) => {
    if (previousMediaConfig && mediaConfig === previousMediaConfig) {
      return;
    }

    // as soon as we have a new media config, we show the headline
    showHeadlineAndInitializeHiding(player);

    if (player.isDestroyed()) {
      return;
    }

    // Pause the previous medium
    if (!firstMedium) {
      player.pause();
    }

    // We need to set the loading state in advance because it can take some time
    // for the player to reach the "loading" session state. Particularly when
    // using the stus and no fully predefined mediaConfig.
    dispatch('load', true);

    player
      .loadMedia({
        ...mediaConfig,
        muted: firstMedium ? mediaConfig.muted : $muted,
        textTrackLanguage: firstMedium ? mediaConfig.textTrackLanguage : lastSelectedTextTrackLanguage,
        autoplay: true,
      })
      .then(() => {
        firstMedium = false;
        // By assigning the mediaConfig to the previousMediaConfig only on
        // success, the user could try to reload the video just by partly
        // swiping the video out of view and back. Hidden feature.
        previousMediaConfig = mediaConfig;
      })
      .catch((error) => {
        console.error('Error loading media', error);
        dispatch('errorCase', error);
      });
  };

  // Called when player is set or the current MediaConfig changes
  $: if (player && currentMediaConfig) {
    loadMedium(player, currentMediaConfig);
  }

  // we mainly use this to hide the controls as long as the medium isn't loaded yet. Currently the player does not support
  // mute() or pause() if the media isn't finished loading. Once this is done this can be removed
  $: mediumCanBeControlled =
    !loading && ($sessionState === 'ready' || $sessionState === 'active' || $sessionState === 'ended');
  $: primeButtonVisible = !loading && ($sessionState === 'ended' || ($sessionState === 'active' && !$playing));

  // When we receive an activeTextTrack, it indicates the user selected it, so we save it for future starts.
  // If activeTextTrack changes to undefined, it could be due to two scenarios:
  // 1) The user disabled subtitles. In this case, the previously selected textTrack is still available
  //    in the current video's available tracks, so we save this state.
  // 2) The previously selected textTrack is not available for the current video,
  //    causing the player to fall back to undefined. In this case, we do not save the state.
  $: {
    if ($activeTextTrack) {
      lastSelectedTextTrackLanguage = $activeTextTrack.language;
    } else if (
      lastSelectedTextTrackLanguage &&
      $availableTextTracks.some((track) => track.language === lastSelectedTextTrackLanguage)
    ) {
      lastSelectedTextTrackLanguage = '';
    }
  }

  let togglePlay: () => void;
  let replay: () => void;
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
  class="controls-layer"
  class:has-error={errorOccurred}
  class:linear-ad-playing={$linearAdPlaying}
  on:click|self={$sessionState === 'ended' ? replay : togglePlay}
  {...$$restProps}
>
  {#if $adClip?.hasClickThrough}
    <div class="ad-link-wrapper">
      <AdLink {player} />
    </div>
  {/if}

  {#if player && primeButtonVisible && !errorOccurred}
    <div class="prime-control-overlay">
      <!-- we need the extra container to apply extra styles to the button for users who prefer more contrast -->
      <div class="prime-button">
        {#if $sessionState === 'ended'}
          <ReplayButton {player} size="xl" {focusLayer} focusName="replayPrime" bind:replay>
            <Play aria-hidden={true} />
          </ReplayButton>
        {:else}
          <!-- This is just a visual duplication of the pause button in the controls bar. So we hide it from screen readers. -->
          <PlayPause
            {player}
            size="xl"
            aria-hidden="true"
            style="pointer-events: initial"
            focusName="playPausePrime"
            {focusLayer}
          />
        {/if}
      </div>
    </div>
  {/if}

  <div class="controls">
    {#if $linearAdPlaying}
      <div class="ad-indicator-wrapper">
        <AdIndicator withAdClipCounter={false} />
      </div>
    {:else if currentMediaConfig?.metadata?.headline}
      <p class="headline" class:is-hidden={$playing && headlineHidden && !errorOccurred}>
        {currentMediaConfig.metadata.headline}
      </p>
    {/if}

    <!-- TODO: As of now (02/2025) methods may not be called when the player is still loading the medium.
    When https://rtltech.atlassian.net/browse/PLAYHTML5-748 and the equivalent ticket for "mute" is
   done we can make the buttons visible again before the media finished loading. -->
    {#if player && mediumCanBeControlled && !errorOccurred}
      <div class="buttons">
        {#if $sessionState === 'ended'}
          <ReplayButton {player} {focusLayer} focusName="replay">
            <Play aria-hidden={true} />
          </ReplayButton>
        {:else}
          <PlayPause {player} focusName="playPause" {focusLayer} bind:togglePlay />
        {/if}

        {#if !$linearAdPlaying}
          <div class="share-button-wrapper">
            <ShareButton focusName="share" {focusLayer} />
          </div>
          <div class="text-track-button-wrapper">
            <TextTrackSelector {player} {focusLayer} focusName="textTrack" />
          </div>
        {/if}

        <Volume
          {player}
          orientation="vertical"
          mode={$linearAdPlaying ? 'only-mute-unmute' : 'full'}
          focusName="volume"
          {focusLayer}
        />
      </div>
    {/if}
  </div>

  <div class="progress-wrapper">
    <ReadonlyProgress currentTime={$currentTime} duration={$durationInSeconds} aria-label={progressBarLabel} />
  </div>
</div>

<style module>
  .controls-layer {
    position: absolute;
    inset: 0;

    display: flex;
    flex-direction: column;
    justify-content: end;

    container-type: inline-size;
    container-name: controls-layer;
  }

  .controls-layer:has(.headline:not(.is-hidden)) {
    background-image: var(--controls-gradient);
  }

  .controls-layer.has-error {
    /* in the case of an error we let the poster-image shine through AND show the
    controls on top of these. To not double the gradient we have applied to each
    we have to take one of the gradients away. */
    --controls-gradient: none;
  }

  .controls-layer.linear-ad-playing {
    --slider-track-color: var(--advertisement-color);
  }

  .controls {
    padding: 10px;
    display: grid;
    grid-template: 'headline buttons' / 1fr 44px;
    gap: 20px;
    align-items: end;

    /* A click on the empty part of this element should bubble up to the
     * .controls-layer element to trigger toggling play/pause. */
    pointer-events: none;
  }

  /* add drop shadow to icons */
  .prime-button :global(svg),
  .controls :global(svg) {
    filter: drop-shadow(0 0 4px rgba(0, 0, 0, 0.75));
  }

  .headline {
    grid-area: headline;
    margin: 0;
    padding-left: 10px;
    padding-bottom: 10px;
    line-height: 1.5;
    font-weight: bold;
    font-size: clamp(1em, 0.6cqw + 0.85em, 1.125em);
    word-break: break-word;
    text-shadow: 0 0 1px var(--focus-background);

    /* A click on the headline should NOT bubble up, so that the user could
     * select and copy the text. */
    pointer-events: auto;
  }

  @media (prefers-reduced-motion: no-preference) {
    .headline {
      transition: 0.3s ease-in-out;
      transition-property: opacity, visibility;
    }
  }

  .headline.is-hidden {
    opacity: 0;
    visibility: hidden;
  }

  .buttons {
    grid-area: buttons;
    display: flex;
    /* we use reverse for a more intuitive tabbing order */
    flex-direction: column-reverse;
    gap: 8px;

    /* A buttons area should - of course - handle clicks normally. */
    pointer-events: auto;
  }

  /* prevent jumping of the headline in case of an error */
  .progress-wrapper {
    height: 4px;
  }

  .controls-layer.has-error .progress-wrapper {
    visibility: hidden;
  }

  .prime-control-overlay {
    position: absolute;
    inset: 0;
    pointer-events: none;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .prime-button {
    /* The prime button should - of course - handle clicks normally. */
    pointer-events: auto;
  }

  @media (prefers-contrast: more) {
    .controls,
    .prime-button {
      background: var(--controls-contrast-background);
    }

    .controls :global(svg) {
      /* Since we set a background to increase the contrast, we don't need the
       * drop shadow anymore. */
      filter: none;
    }
  }

  @media (prefers-contrast: more) and (prefers-reduced-transparency: reduce) {
    .controls,
    .prime-button {
      background: var(--controls-no-transparency-background);
    }
  }

  .ad-link-wrapper {
    position: absolute;
    top: 8px;
    right: 4px;
    /* translate is more performant than changing the top position */
    transform: translateY(var(--close-button-overlap, 0));
  }

  .ad-indicator-wrapper {
    /* different from figma because a different box model is used there */
    padding-bottom: 2px;
  }

  .text-track-button-wrapper {
    z-index: 1;
  }

  /* prevent the gap of the flex box to apply to empty wrappers */
  .share-button-wrapper:empty,
  .text-track-button-wrapper:empty {
    display: none;
  }

  .share-button-wrapper {
    z-index: 2;
  }
</style>
